import {
    Alert,
    AlertContent,
    Button,
    DateField,
    Modal,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalLayout,
    ModalTitle,
    MoneyField,
    PercentageField,
    TextField,
} from '@tlx/atlas';
import { Dispatch, SetStateAction, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { APIError, ResponseWrapper, showActionLogMessage } from '@tlx/astro-shared';

import {
    AttributeDropdown,
    getNotChosenOption,
    getWageSpecificationTemplateTypeOptions,
} from '@Component/AttributeDropdown/AttributeDropdown';
import { UncontrolledForm } from '@Component/Form/UncontrolledForm';
import { SalaryV2TransactionDTO, WageCodeDTO } from '@Models/model.types';
import { callApi, computeUrl, mapObjectToUrlSearchParams, MethodType } from '@Util/api';
import { mapValidationMessages } from '@Util/apiErrorUtils.ts';

import { SpinnerButton } from '@tlx/tsk-shared';
import { dateUtil } from '@Util/date.ts';
import { createOrdinalFromArray } from '@Util/misc.ts';
import { validateInteger, validateNumber } from '@Util/numberUtils';
import { SanitizedHTMLAndMarkdown } from '@Util/SanitizedHTMLAndMarkdown.tsx';
import { APIUrls, DropdownUrls } from '@Util/urls';

export type AdditionsDeductionsDTO = {
    employeeIds: string;
    wageSpecificationTemplate: string;
};

export type WageSpecificationTemplateDTO = {
    type: string;
    wageCodeId: string;
    startDate: string;
    endDate: string;
    amount: string;
    maxPaymentAmount: string;
    percentage: string;
    description: string;
    wageSpecificationExtra?: WageSpecificationExtraDTO;
};

export type WageSpecificationExtraDTO = {
    carListPrice?: string;
    carRegistrationNumber?: string;
};

const showMonthly = ['', '1', '2'];

export function AdditionsDeductionsModal({
    open,
    onClose,
    employeeIds,
    setValidationsMap,
}: {
    open: boolean;
    onClose: () => void;
    employeeIds: number[];
    setValidationsMap: Dispatch<SetStateAction<Map<number, string[]>>>;
}) {
    const formId = 'AdditionsDeductionsForm';
    const intl = useIntl();
    const [awaitingResponse, setAwaitingResponse] = useState<boolean>(false);
    const [errorsToShowInModal, setErrorsToShowInModal] = useState<string[]>([]);
    const [currentType, setCurrentType] = useState<string>('');
    const [requiredSupplementFields, setRequiredSupplementFields] = useState<string[]>([]);

    const submitForm = async (_: HTMLFormElement, formData: { [p: string]: FormDataEntryValue }) => {
        setValidationsMap(new Map());
        setAwaitingResponse(true);
        const wageSpecificationTemplateDTO: WageSpecificationTemplateDTO = {
            type: formData.typeDropdown as string,
            wageCodeId: formData.wageCodeDropdown as string,
            startDate: formData.startDate as string,
            endDate: formData.endDate as string,
            amount: formData.amount as string,
            percentage: formData.percentage as string,
            maxPaymentAmount: formData.maxPaymentAmount as string,
            description: formData.description as string,
        };
        if (requiredSupplementFields.length > 0) {
            wageSpecificationTemplateDTO.wageSpecificationExtra = {
                carListPrice: formData.carListPrice as string,
                carRegistrationNumber: formData.carRegistrationNumber as string,
            };
        }
        const AdditionsDeductionsDTO: AdditionsDeductionsDTO = {
            employeeIds: employeeIds.join(','),
            wageSpecificationTemplate: JSON.stringify(wageSpecificationTemplateDTO),
        };
        const { response, error } = await callApi<ResponseWrapper<SalaryV2TransactionDTO>>(
            computeUrl(
                APIUrls.ADDITIONAL_DEDUCTIONS,
                mapObjectToUrlSearchParams({
                    employeeIds: AdditionsDeductionsDTO.employeeIds,
                }),
            ),
            MethodType.POST,
            JSON.stringify(wageSpecificationTemplateDTO),
        );
        setAwaitingResponse(false);
        if (error) {
            if (error instanceof APIError) {
                mapValidationMessages(
                    error.details.validationMessages,
                    setValidationsMap,
                    setErrorsToShowInModal,
                    intl.formatMessage({
                        id: 'validation_unexpected_error',
                    }),
                    onClose,
                );
            }
        }
        if (response) {
            onClose();
            showActionLogMessage({
                pageName: intl.formatMessage({
                    id: 'heading_employee_overview',
                }),
                date: new Date(),
                message: intl.formatMessage(
                    {
                        id:
                            employeeIds.length === 1
                                ? 'tsk_additions_deductions_success_singular'
                                : 'tsk_change_employment_success_plural',
                    },
                    createOrdinalFromArray([employeeIds.length]),
                ),
            });
        }
    };

    const handleChangeWageCode = async (id: string) => {
        if (id !== '') {
            const { response } = await callApi<ResponseWrapper<WageCodeDTO>>(
                computeUrl(
                    `${APIUrls.WAGE_CODE}/${id}`,
                    mapObjectToUrlSearchParams({
                        fields: 'id, requiredSupplementFields',
                    }),
                ),
                MethodType.GET,
            );
            if (response) {
                setRequiredSupplementFields(response.value.requiredSupplementFields);
            }
        } else {
            setRequiredSupplementFields([]);
        }
    };

    return (
        <Modal
            open={open}
            onClose={() => {
                if (!awaitingResponse) {
                    // to avoid user "cancelling" the dialog by clicking outside it
                    onClose();
                }
            }}
        >
            <ModalLayout>
                {!awaitingResponse && <ModalCloseButton />}
                <ModalTitle>
                    <FormattedMessage id={'text_add_standard_additions_deductions'} />
                </ModalTitle>
                <ModalContent className={'tsk-width-600'}>
                    {errorsToShowInModal.length > 0 && (
                        <Alert variant="error" className={'atl-mb-24'}>
                            <AlertContent>
                                <SanitizedHTMLAndMarkdown content={errorsToShowInModal.join('\n')} />
                            </AlertContent>
                        </Alert>
                    )}
                    <UncontrolledForm submitForm={submitForm} id={formId}>
                        <div className="atl-flex atl-flex-col atl-gap-24">
                            <div className={'atl-flex atl-gap-x-16'}>
                                <AttributeDropdown
                                    text={'text_type'}
                                    id={'typeDropdown'}
                                    options={[
                                        ...getNotChosenOption(intl),
                                        ...getWageSpecificationTemplateTypeOptions(intl),
                                    ]}
                                    onChange={setCurrentType}
                                    isRequired
                                />
                                <AttributeDropdown
                                    text={'text_wage_code'}
                                    id={'wageCodeDropdown'}
                                    url={DropdownUrls.WAGE_CODES}
                                    options={getNotChosenOption(intl)}
                                    onChange={handleChangeWageCode}
                                    isRequired
                                />
                            </div>
                            {requiredSupplementFields.length > 0 && (
                                <ExtraInfoFields requiredSupplementFields={requiredSupplementFields} />
                            )}
                            <div className={'atl-flex atl-gap-x-16'}>
                                <DateField
                                    className={'atl-w-full'}
                                    data-testid={'start-date-input'}
                                    name={'startDate'}
                                    label={intl.formatMessage({
                                        id: 'text_start_date',
                                    })}
                                    isRequired
                                    validate={(value) => dateUtil.validateDateString(value, intl)}
                                />
                                <DateField
                                    className={'atl-w-full'}
                                    data-testid={'end-date-input'}
                                    name={'endDate'}
                                    label={intl.formatMessage({
                                        id: 'text_end_date',
                                    })}
                                    validate={(value) => dateUtil.validateDateString(value, intl)}
                                />
                            </div>
                            <div className={'atl-flex atl-gap-x-16'}>
                                {showMonthly.includes(currentType) ? (
                                    <MoneyField
                                        className={'atl-w-full'}
                                        data-testid={'monthly-amount-input'}
                                        name={'amount'}
                                        label={intl.formatMessage({
                                            id: 'text_monthly_amount',
                                        })}
                                    />
                                ) : (
                                    <PercentageField
                                        className={'atl-w-full'}
                                        data-testid={'percentage-input'}
                                        name={'percentage'}
                                        label={intl.formatMessage({
                                            id: 'text_percentage',
                                        })}
                                        isRequired
                                    />
                                )}
                                <MoneyField
                                    label={intl.formatMessage({
                                        id: 'text_max_total_amount',
                                    })}
                                    className={'atl-w-full'}
                                    data-testid={'max-input'}
                                    name={'maxPaymentAmount'}
                                    validate={(value) => validateNumber(intl, value)}
                                />
                            </div>
                            <TextField
                                label={intl.formatMessage({
                                    id: 'text_description',
                                })}
                                className={'atl-w-full tsk-flex-50'}
                                data-testid={'description-input'}
                                name={'description'}
                            />
                        </div>
                    </UncontrolledForm>
                </ModalContent>
                <ModalFooter className={'atl-flex atl-gap-8'}>
                    <SpinnerButton
                        className="atl-button--primary"
                        data-testid="change-employee-info-run-button"
                        type={'submit'}
                        form={formId}
                        disabled={awaitingResponse}
                        isLoading={awaitingResponse}
                    >
                        <FormattedMessage id={'button_save'} />
                    </SpinnerButton>
                    <Button
                        variant={'secondary'}
                        onClick={onClose}
                        data-testid="change-employee-info-modal-cancel-button"
                        disabled={awaitingResponse}
                    >
                        <FormattedMessage id={'text_cancel'} />
                    </Button>
                </ModalFooter>
            </ModalLayout>
        </Modal>
    );
}

function ExtraInfoFields({ requiredSupplementFields }: { requiredSupplementFields: string[] }) {
    const intl = useIntl();
    return (
        <div className={'atl-flex atl-gap-x-16 atl-mb-24'}>
            {requiredSupplementFields.includes('CAR_LIST_PRICE') && (
                <MoneyField
                    label={intl.formatMessage({
                        id: 'text_car_list_price',
                    })}
                    className={'atl-w-full tsk-flex-50'}
                    data-testid={'carListPrice-input'}
                    name={'carListPrice'}
                    isRequired
                />
            )}
            {requiredSupplementFields.includes('CAR_REGISTRATION_NUMBER') && (
                <TextField
                    label={intl.formatMessage({
                        id: 'text_car_reg_number',
                    })}
                    className={'atl-w-full tsk-flex-50'}
                    data-testid={'carRegistrationNumber-input'}
                    name={'carRegistrationNumber'}
                    isRequired
                />
            )}
            {requiredSupplementFields.includes('COUNTRY') && (
                <AttributeDropdown
                    text={'text_country'}
                    id={'countryDropdown'}
                    url={DropdownUrls.COUNTRIES}
                    options={getNotChosenOption(intl)}
                    isRequired
                />
            )}
            {requiredSupplementFields.includes('TAX_COUNTRY') && (
                <AttributeDropdown
                    text={'text_tax_country'}
                    id={'taxCountryDropdown'}
                    url={DropdownUrls.COUNTRIES}
                    options={getNotChosenOption(intl)}
                    isRequired
                />
            )}
            {requiredSupplementFields.includes('UPGROSSING_TABLE_NUMBER') && (
                <TextField
                    label={intl.formatMessage({
                        id: 'text_upgrossing_table_number',
                    })}
                    className={'atl-w-full tsk-flex-50'}
                    data-testid={'upgrossingTableNumber-input'}
                    name={'upgrossingTableNumber'}
                    isRequired
                    validate={(value) => validateInteger(value, intl)}
                />
            )}
        </div>
    );
}
